interface Breakpoints {
  small: number
  medium: number
  big: number
  large: number
  xtralarge: number
}
type BreakpointName = keyof Breakpoints

export const breakpoints: Breakpoints = {
  small: 360 - 1,
  medium: 480 - 1,
  big: 768 - 1,
  large: 992 - 1,
  xtralarge: 1200 - 1,
}

export const mediaQueries = [
  { name: 'xtralarge', query: `(min-width: ${breakpoints.xtralarge}px)` },
  { name: 'large', query: `(min-width: ${breakpoints.large}px)` },
  { name: 'big', query: `(min-width: ${breakpoints.big}px)` },
  { name: 'medium', query: `(min-width: ${breakpoints.medium}px)` },
  { name: 'small', query: `(min-width: ${breakpoints.small}px)` },
]

/**
 * Retrieves the numeric value of a specified breakpoint name.
 *
 * @param name - The name of the breakpoint to retrieve.
 * @returns The numeric value of the breakpoint if it exists, otherwise `null`.
 */
export const getBreakpointValue = (name: string): number | null => breakpoints[name] || null

/**
 * Retrieves the name of the current breakpoint based on the active media query.
 *
 * @returns {BreakpointName} The name of the current breakpoint. Defaults to 'small' if no media query matches.
 */
export const getCurrentBreakpointName = (): BreakpointName =>
  (mediaQueries.find(({ query }) => window.matchMedia(query).matches)?.name ?? 'small') as BreakpointName

/**
 * Checks if the specified breakpoint is currently active.
 *
 * @param {BreakpointName} breakpointName - The name of the breakpoint to check.
 * @returns {boolean} - Returns `true` if the breakpoint is active, otherwise `false`.
 */
export const isActiveBreakpoint = (breakpointName: BreakpointName): boolean => {
  const mediaQuery = mediaQueries.find(({ name }) => name === breakpointName)
  if (!mediaQuery) return false

  return window.matchMedia(mediaQuery.query).matches
}

/**
 * Retrieves the MediaQueryList for a given breakpoint name.
 *
 * @param {BreakpointName} breakpointName - The name of the breakpoint to retrieve the media query for.
 * @returns {MediaQueryList | null} - The MediaQueryList object if the breakpoint is found, otherwise null.
 */
export const getBreakpointMediaQuery = (breakpointName: BreakpointName): MediaQueryList | null => {
  const mediaQuery = mediaQueries.find(({ name }) => name === breakpointName)
  return mediaQuery ? window.matchMedia(mediaQuery.query) : null
}
